import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, Tag, Avatar, List, Spin } from 'antd';
import {
  TeamOutlined, UserAddOutlined, RiseOutlined, CheckCircleOutlined,
  WarningOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { customerApi, logApi, quotationApi, contractApi, warehouseApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STAGE_COLORS = {
  '新客户': 'blue', '意向客户': 'orange', '报价阶段': 'purple',
  '已成交': 'green', '已流失': 'red', '跟进中': 'gold',
};

const LOG_TYPE_COLORS = {
  '电话': 'blue', '微信': 'green', '拜访': 'purple',
  '报价': 'orange', '成交': 'success', '备注': 'default',
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [stockWarning, setStockWarning] = useState(0);
  const [expiringContracts, setExpiringContracts] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      customerApi.stats(),
      customerApi.list({ pageSize: 5 }),
      logApi.list({ pageSize: 5 }),
      // 获取库存预警数量
      warehouseApi.inventory({ stockStatus: 'low', pageSize: 999 }).catch(() => ({ data: [] })),
      warehouseApi.inventory({ stockStatus: 'out', pageSize: 999 }).catch(() => ({ data: [] })),
      // 获取合同列表（计算即将到期）
      contractApi.list({ pageSize: 999 }).catch(() => ({ data: [] })),
    ]).then(([s, c, l, lowStock, outStock, contracts]) => {
      setStats(s);
      setRecentCustomers(c.data);
      setRecentLogs(l.data);
      setStockWarning((lowStock.total || 0) + (outStock.total || 0));
      // 计算30天内到期合同
      const now = dayjs();
      const expiring = (contracts.data || []).filter((ct) => {
        if (!ct.endAt) return false;
        const end = dayjs(ct.endAt);
        const diff = end.diff(now, 'day');
        return diff >= 0 && diff <= 30 && ct.status !== '已终止' && ct.status !== '已完成';
      }).length;
      setExpiringContracts(expiring);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const funnel = stats?.funnel || {};
  const total = stats?.total || 1;

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>数据看板</Title>

      {/* 指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: '总客户数', value: stats?.total, icon: <TeamOutlined />, color: '#1890ff' },
          { title: '本月新增', value: stats?.thisMonth, icon: <UserAddOutlined />, color: '#07c160' },
          { title: '跟进中', value: stats?.following, icon: <RiseOutlined />, color: '#fa8c16' },
          { title: '本月成交', value: stats?.dealThisMonth, icon: <CheckCircleOutlined />, color: '#52c41a' },
          { title: '库存预警', value: stockWarning, icon: <WarningOutlined />, color: stockWarning > 0 ? '#fa8c16' : '#52c41a' },
          { title: '即将到期合同', value: expiringContracts, icon: <ClockCircleOutlined />, color: expiringContracts > 0 ? '#cf1322' : '#52c41a' },
        ].map((item) => (
          <Col xs={12} sm={6} md={4} key={item.title}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title={item.title}
                value={item.value ?? 0}
                valueStyle={{ color: item.color, fontWeight: 500 }}
                prefix={item.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* 销售漏斗 */}
      <Card title="销售漏斗" bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          {[
            { label: '新客户', key: '新客户', color: '#1890ff' },
            { label: '意向客户', key: '意向客户', color: '#fa8c16' },
            { label: '报价阶段', key: '报价阶段', color: '#722ed1' },
            { label: '已成交', key: '已成交', color: '#52c41a' },
          ].map((stage) => {
            const count = funnel[stage.key] || 0;
            const pct = Math.round((count / total) * 100);
            return (
              <Col xs={12} sm={6} key={stage.key}>
                <div style={{ textAlign: 'center', padding: '16px 8px', border: '1px solid #f0f0f0', borderRadius: 10 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{stage.label}</Text>
                  <div style={{ fontSize: 28, fontWeight: 500, margin: '4px 0', color: stage.color }}>{count}</div>
                  <Text style={{ fontSize: 12, color: stage.color }}>{pct}%</Text>
                  <div style={{ height: 4, borderRadius: 2, background: stage.color, opacity: 0.3, marginTop: 10 }} />
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        {/* 最近添加客户 */}
        <Col xs={24} md={12}>
          <Card title="最近添加" bordered={false} style={{ borderRadius: 12 }}>
            <List
              dataSource={recentCustomers}
              renderItem={(c) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar style={{ background: '#1890ff' }}>{c.name[0]}</Avatar>}
                    title={<span>{c.name} <Text type="secondary" style={{ fontSize: 12 }}>{c.company}</Text></span>}
                    description={c.phone || '—'}
                  />
                  <Tag color={STAGE_COLORS[c.stage] || 'default'}>{c.stage}</Tag>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 最近跟进记录 */}
        <Col xs={24} md={12}>
          <Card title="最近跟进" bordered={false} style={{ borderRadius: 12 }}>
            <List
              dataSource={recentLogs}
              renderItem={(log) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <span>
                        {log.customer?.name}
                        <Tag color={LOG_TYPE_COLORS[log.type] || 'default'} style={{ marginLeft: 8 }}>{log.type}</Tag>
                      </span>
                    }
                    description={
                      <Text ellipsis style={{ maxWidth: 200 }}>{log.content}</Text>
                    }
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(log.createdAt).toLocaleDateString('zh-CN')}
                  </Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
